import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { cloneDeep, Dictionary } from 'lodash';
import { catchError, filter, map, of, switchMap } from 'rxjs';

import { User } from '@ninety/platform/models/_shared/user';
import { LinkedItemMessageType } from '@ninety/platform/models/linked-items/linked-item-message-type';
import {
  LinkedItemTypeEnum,
  linkedItemTypeToDetailTypesMap,
} from '@ninety/platform/models/linked-items/linked-item-type-enum';
import type { LinkedItems } from '@ninety/platform/models/linked-items/linked-items-response.model';
import {
  LinkedItemFromRealtime,
  UnlinkedItemFromRealtime,
} from '@ninety/platform/models/linked-items/linked-items.model';
import { ChannelService } from '@ninety/platform/services';
import { RealTimeActions, selectIsMyNinetyUrl, selectRouteIsAnyOfDetailType } from '@ninety/platform/state';
import * as UserSelectors from '@ninety/platform/state/app-entities/users/users-state.selectors';

import { LinkedExistingItemsDialogComponent } from '../../linked-existing-items/linked-existing-items-dialog.component';
import { LinkedItemsService } from '../linked-items.service';

import { LinkedItemsActions } from './linked-items.actions';
import { LinkedItemsSelectors } from './linked-items.selectors';

@Injectable()
export class LinkedItemsEffects {
  private actions$ = inject(Actions);
  private linkedItemsService = inject(LinkedItemsService);
  private store = inject(Store);
  private channelService = inject(ChannelService);
  private readonly dialog = inject(MatDialog);

  getLinkedItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkedItemsActions.getLinkedItem),
      concatLatestFrom(() => [this.store.select(UserSelectors.selectUserEntities)]),
      switchMap(([{ id, linkedItemType }, users]) =>
        this.linkedItemsService.getLinkedItems(linkedItemType, id).pipe(
          map(linkedItems => this.getItemsFromResponse(linkedItems, users)),
          catchError((error: unknown) => of(LinkedItemsActions.getLinkedItemFailure({ error })))
        )
      )
    )
  );

  // Rows added after a create or a meeting broadcast have no linkId, which Unlink needs.
  // To-do links are written later by an event (SNS -> SQS -> linked items lambda), so a to-do created just
  // before Edit can be missing from this reload. Possible fix: write the link in the create request.
  refreshItemsWithoutLinkIdOnEdit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkedItemsActions.editModeOpened),
      concatLatestFrom(() => [
        this.store.select(LinkedItemsSelectors.selectHasItemsWithoutLinkId),
        this.store.select(LinkedItemsSelectors.selectId),
        this.store.select(LinkedItemsSelectors.selectType),
      ]),
      filter(([, hasItemsWithoutLinkId]) => hasItemsWithoutLinkId),
      map(([, , id, linkedItemType]) => LinkedItemsActions.getLinkedItem({ id, linkedItemType }))
    )
  );

  unlinkItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkedItemsActions.unlinkItem),
      switchMap(({ id, linkedItemType }) =>
        this.linkedItemsService.deleteLinkedItem(id).pipe(
          map(_ => LinkedItemsActions.unlinkItemSuccess({ id, linkedItemType })),
          catchError((error: unknown) => of(LinkedItemsActions.unlinkItemFailure({ error })))
        )
      )
    )
  );

  broadcastUnlinkedItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkedItemsActions.unlinkItemSuccess),
      concatLatestFrom(() => [this.store.select(LinkedItemsSelectors.selectId)]),
      map(([{ id, linkedItemType }, _id]) =>
        LinkedItemsActions.broadcastMessage({
          message: {
            messageType: LinkedItemMessageType.unlinkItem,
            document: {
              id: _id,
              linkedItemType,
              linkedItemId: id,
            },
          },
        })
      )
    )
  );

  broadcastLinkedItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkedItemsActions.createdLinkedItem),
      map(({ linkedItem: { id, linkedItemType, linkedItemId, linkedItem } }) =>
        LinkedItemsActions.broadcastMessage({
          message: {
            messageType: LinkedItemMessageType.linkedItem,
            document: {
              id,
              linkedItemType,
              linkedItemId,
              linkedItem,
            },
          },
        })
      )
    )
  );

  broadcastMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkedItemsActions.broadcastMessage),
      concatLatestFrom(() => [
        this.store.select(LinkedItemsSelectors.selectShouldBroadcast),
        this.store.select(LinkedItemsSelectors.selectChannelId),
      ]),
      filter(([, shouldBroadcast]) => shouldBroadcast),
      switchMap(([{ message }, , channelId]) =>
        this.channelService.sendMessage(channelId, cloneDeep(message)).pipe(
          map(_ => LinkedItemsActions.broadcastMessageSuccess()),
          catchError((error: unknown) => of(LinkedItemsActions.broadcastMessageFailure({ error })))
        )
      )
    )
  );

  createdLinkedItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkedItemsActions.createdLinkedItem),
      concatLatestFrom(({ createdFrom }) => [
        this.store.select(LinkedItemsSelectors.selectId),
        this.store.select(selectRouteIsAnyOfDetailType(linkedItemTypeToDetailTypesMap[createdFrom])),
        this.store.select(selectIsMyNinetyUrl),
        this.store.select(UserSelectors.selectUserEntities),
      ]),
      filter(([{ linkedItem }, _id, isType, isMy90]) => linkedItem.id === _id && (isType || isMy90)),
      map(([{ linkedItem }, , , , users]) => {
        const userIdKey = 'ownedByUserId' in linkedItem.linkedItem ? 'ownedByUserId' : 'userId';
        const [itemWithUser] = this.mapItemsWithUser(users, [linkedItem.linkedItem], userIdKey);
        return LinkedItemsActions.displayCreatedLinkedItem({ linkedItem: { ...linkedItem, linkedItem: itemWithUser } });
      })
    )
  );

  realtimeReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      concatLatestFrom(() => [this.store.select(LinkedItemsSelectors.selectChannelId)]),
      filter(
        ([{ channelId, message }, currentChannelId]) =>
          channelId === currentChannelId &&
          (message.messageType === LinkedItemMessageType.linkedItem ||
            message.messageType === LinkedItemMessageType.unlinkItem)
      ),
      concatLatestFrom(() => [this.store.select(LinkedItemsSelectors.selectId)]),
      filter(([[{ message }], _id]) => (message.document as UnlinkedItemFromRealtime).id === _id),
      map(([[{ message }], _id]) => {
        if (message.messageType === LinkedItemMessageType.unlinkItem) {
          const mess = message.document as UnlinkedItemFromRealtime;
          return LinkedItemsActions.unlinkItemFromRealtime({
            id: mess.linkedItemId,
            linkedItemType: mess.linkedItemType,
          });
        } else {
          return LinkedItemsActions.linkItemFromRealtime({
            linkedItem: message.document as LinkedItemFromRealtime,
          });
        }
      })
    )
  );

  openLinkedExistingItemsDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LinkedItemsActions.openLinkedExistingItemsDialog),
        switchMap(
          ({ fromId, linkedItemType, title, teamId, openDialogWithItemTypeTabSelected, selectedLinkedItemTabType }) =>
            this.dialog
              .open(LinkedExistingItemsDialogComponent, {
                panelClass: 'linked-existing-items__dialog',
                data: {
                  fromId,
                  linkedItemType,
                  title,
                  teamId,
                  openDialogWithItemTypeTabSelected,
                  selectedLinkedItemTabType: selectedLinkedItemTabType,
                },
                autoFocus: false,
                disableClose: true,
              })
              .afterClosed()
        )
      ),
    { dispatch: false }
  );

  addLinkedItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkedItemsActions.addLinkedItems),
      concatLatestFrom(() => [this.store.select(UserSelectors.selectUserEntities)]),
      switchMap(([{ linkedItems, fromType, fromId }, users]) =>
        this.linkedItemsService.addLinkedItems(linkedItems, fromType, fromId).pipe(
          map(linkedItems => this.getItemsFromResponse(linkedItems, users, fromId, fromType)),
          catchError((error: unknown) => of(LinkedItemsActions.addLinkedItemsFailure({ error })))
        )
      )
    )
  );

  private mapItemsWithUser = (users, items, userIdKey) =>
    items.map(item => {
      const user = users[item[userIdKey]];
      return { ...item, user };
    });

  private getItemsFromResponse = (
    linkedItems: LinkedItems,
    users: Dictionary<User>,
    fromId?: string,
    fromType?: LinkedItemTypeEnum
  ) => {
    linkedItems.todos = this.mapItemsWithUser(users, linkedItems.todos, 'userId');
    linkedItems.issues = this.mapItemsWithUser(users, linkedItems.issues, 'userId');
    linkedItems.rocks = this.mapItemsWithUser(users, linkedItems.rocks, 'userId');
    linkedItems.headlines = this.mapItemsWithUser(users, linkedItems.headlines, 'ownedByUserId');
    linkedItems.cascadingMessages = this.mapItemsWithUser(users, linkedItems.cascadingMessages, 'ownedByUserId');
    linkedItems.learningTasks = this.mapItemsWithUser(users, linkedItems.learningTasks || [], 'ownedByUserId');
    linkedItems.learningTopics = this.mapItemsWithUser(users, linkedItems.learningTopics || [], 'ownedByUserId');
    linkedItems.milestones = this.mapItemsWithUser(users, linkedItems.milestones, 'ownedByUserId');
    return LinkedItemsActions.getLinkedItemSuccess({ response: linkedItems, fromId, fromType });
  };
}
